import { FormikErrors, useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import VoxelAutocompleteLanguage, {
  allLanguagesOption,
} from '../../../components/muiLegacy/Autocomplete/VoxelAutocompleteLanguage';
import VoxelLink from '../../../components/voxel/Link/VoxelLink';
import VoxelFormPanel from '../../../components/voxel/Surfaces/VoxelFormPanel/VoxelFormPanel';
import { GoogleAdsLanguageGq } from '../../../graphql/projects/models/GoogleAdsLanguageGq';
import { ProjectGq } from '../../../graphql/projects/models/ProjectGq';
import { projectsApiService } from '../../../graphql/projects/projectsApiService';
import { useProjectDefaults } from '../../../logic/otherUtilities/useProjectDefaults';
import VoxelPanelActions from '../../../components/voxel/Surfaces/VoxelFormPanel/VoxelPanelActions';
import VoxelFormPanelActionCancel from '../../../components/voxel/Surfaces/VoxelFormPanel/VoxelFormPanelActionCancel';
import VoxelFormPanelActionSubmit from '../../../components/voxel/Surfaces/VoxelFormPanel/VoxelFormPanelActionSubmit';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faUnlock } from '@fortawesome/pro-regular-svg-icons';
import LockIcon from '../../../components/voxel/Icons/LockIcon';
import VoxelAlertDialog from '../../../components/voxel/Dialogs/VoxelAlertDialog';
import VoxelDialogContent from '../../../components/voxel/Dialogs/VoxelDialogContent';
import VoxelText from '../../../components/voxel/Text/VoxelText';
import VoxelDialogActions from '../../../components/voxel/Dialogs/VoxelDialogActions';
import VoxelDialogActionCancel from '../../../components/voxel/Dialogs/VoxelDialogActionCancel';
import VoxelDialogActionSubmit from '../../../components/voxel/Dialogs/VoxelDialogActionSubmit';
import VoxelFormPanelContent from '../../../components/voxel/Surfaces/VoxelFormPanel/VoxelFormPanelContent';

export interface FormState {
  language: GoogleAdsLanguageGq;
  rankLanguage: GoogleAdsLanguageGq;
  otherRankSettings: boolean;
}

export interface LanguageFormProps {
  project: ProjectGq;
  setDirty: (dirty: boolean) => void;
  setSave: (saveFunction: () => Promise<unknown>) => void;
  reload: () => Promise<unknown>;
  disabled?: boolean;
}

const LanguageForm: React.FC<LanguageFormProps> = ({
  project,
  setDirty,
  setSave,
  reload,
  disabled,
}) => {
  // Initial form values
  const projectDefaults = useProjectDefaults(project);

  const defaultIsAllLanguages = !projectDefaults.volumeLanguage;
  const serpLanguageRequired = defaultIsAllLanguages && !projectDefaults.serpLanguage;
  const formIsDefaultLocked =
    (project.projectHasKeywordRequests || project.progressHasSerp) && !serpLanguageRequired;

  const [formLocked, setFormLocked] = useState(formIsDefaultLocked);
  const [openUnlockWarning, setOpenUnlockWarning] = useState(false);

  const mutation = projectsApiService.mutations.projects.useUpdateProjectLanguage();
  const formikProps = useFormik<FormState>({
    initialValues: {
      language: projectDefaults.volumeLanguage ?? allLanguagesOption,
      rankLanguage: projectDefaults.serpLanguage,
      otherRankSettings: !projectDefaults.volumeLanguage,
    },
    validate: (data) => {
      const errors: FormikErrors<FormState> = {};
      return errors;
    },
    enableReinitialize: true,
    onSubmit: (values, actions) =>
      mutation({
        projectId: project.id,
        languageId: values.language?.id === allLanguagesOption.id ? null : values.language.id,
        languageName:
          values.language?.name === allLanguagesOption.name ? null : values.language.name,
        rankLanguageId:
          values.language?.id === allLanguagesOption.id
            ? values.rankLanguage.id
            : values.language.id,
        rankLanguageName:
          values.language?.name === allLanguagesOption.name
            ? values.rankLanguage.name
            : values.language.name,
      })
        .catch((error) => console.error(error))
        .then(() => setFormLocked(true))
        .then(() => reload()),
  });

  useEffect(() => setSave(formikProps.submitForm), [formikProps.submitForm]);
  useEffect(() => {
    setDirty(formikProps.dirty);
    return () => setDirty(false);
  }, [formikProps.dirty]);

  return (
    <VoxelFormPanel
      disabled={disabled}
      title={
        <span className={'flex flex-row items-center gap-2'}>
          {!formLocked ? null : <LockIcon />}Language
        </span>
      }
      subtitle={
        <span>
          The language settings are based the list of{' '}
          <VoxelLink
            openInNewIcon
            openInNewTab
            href="https://developers.google.com/google-ads/api/data/codes-formats"
          >
            languages
          </VoxelLink>{' '}
          made availabe by Google Ads.{' '}
          <VoxelLink
            openInNewTab
            href="https://support.google.com/google-ads/answer/1722078?hl=en&ref_topic=3119074&sjid=8646136592463639870-EU"
          >
            Read more
          </VoxelLink>{' '}
          at Google.
        </span>
      }
    >
      <VoxelFormPanelContent disabled={disabled}>
        <div className="w-full flex flex-row gap-4">
          <VoxelAutocompleteLanguage
            type="both"
            labelPosition="top"
            selectedLanguage={formikProps.values.language}
            setSelectedLanguage={(language) => formikProps.setFieldValue('language', language)}
            disabled={formLocked || formikProps.isSubmitting}
            inputWidthClass="w-full"
          />
          {formikProps.values.language?.id === allLanguagesOption.id && (
            <VoxelAutocompleteLanguage
              type="rank"
              labelPosition="top"
              selectedLanguage={formikProps.values.rankLanguage}
              setSelectedLanguage={(language) =>
                formikProps.setFieldValue('rankLanguage', language)
              }
              disabled={formLocked || formikProps.isSubmitting}
              inputWidthClass="w-full"
              helperText={!formikProps.values.rankLanguage ? 'Required' : null}
              showHelperText={!formikProps.values.rankLanguage}
            />
          )}
        </div>
      </VoxelFormPanelContent>
      <VoxelPanelActions>
        {formLocked && (
          <VoxelFormPanelActionSubmit
            color="gray"
            onClick={() => setOpenUnlockWarning(true)}
            title={'Unlock to edit'}
            endIcon={<FontAwesomeIcon icon={faUnlock} />}
            disabled={disabled}
          />
        )}
        {!formLocked && formIsDefaultLocked && (
          <VoxelFormPanelActionCancel onClick={() => setFormLocked(true)} />
        )}
        {!formLocked && (
          <VoxelFormPanelActionSubmit
            title={'Save changes'}
            onClick={formikProps.submitForm}
            disabled={formikProps.isSubmitting || !formikProps.isValid || !formikProps.dirty}
            endIcon={<FontAwesomeIcon icon={faSave} />}
          />
        )}
      </VoxelPanelActions>
      <VoxelAlertDialog
        title="Data loss"
        severity="warning"
        open={openUnlockWarning}
        onClose={() => setOpenUnlockWarning(false)}
      >
        <VoxelDialogContent>
          <VoxelText>
            <span>
              Editing will trigger a deletion of any keyword or rank data that is not consistent
              with the new setting. This includes keywords that have been added using either any of
              the related or the autocomplete option.
            </span>
            <br></br>
            <br></br>
            This is to ensure data consistency.
          </VoxelText>
        </VoxelDialogContent>
        <VoxelDialogActions>
          <VoxelDialogActionCancel onClick={() => setOpenUnlockWarning(false)} />
          <VoxelDialogActionSubmit
            color="gray"
            onClick={() => {
              setFormLocked(false);
              setOpenUnlockWarning(false);
            }}
            title={'Proceed'}
          />
        </VoxelDialogActions>
      </VoxelAlertDialog>
    </VoxelFormPanel>
  );
};

export default LanguageForm;
